import { BehaviorSubject, map, Observable } from "rxjs";
import * as d3 from "d3";
import {DSVRowArray} from "d3";
import * as xlsx from "xlsx-js-style";
import { ExportTableStructure } from "../table-structure/export-table-structure";
import { cellBodyFormat, cellHeaderFormat, ICellFormattingStrategies } from "../cell-format/export-cell-value-formatting-strategies";
import { DashboardFilterController } from "../../filters/dashboard_filter_controller";
import { DashboardMultiSelectFilter, DashboardSingleSelectFilter } from "../../filters/dashboard_select_filter";
import { getFilenameDatePrefix } from "src/app/shared/utils/get-filename-date-prefix";
import { DashboardRangeFilter } from "../../filters/dashboard_range_filter";
import { DashboardUtils, DecodedDashboardId } from "src/app/dashboards/DashboardUtils";


export interface ExportTableMetadata {
  productName?: string;
  dashboardName?: string;
  dashboardVariant?: string;
  dataSource?: string;
  dashboardId: string;
  exportSide?: string;
}

type MergeObject = {
  s: {
    r: number;
    c: number;
  };
  e: {
    r: number;
    c: number;
  };
};

export type RawCell = {
  value: string | null | undefined;
  rowSpan: number;
  cn: number;
  colSpan: number
};

export class DashboardExportController {

  private readonly _tableDataSubject: BehaviorSubject<any[] | null>;
  private readonly _tableDataNormalizedSubject: BehaviorSubject<any[] | null>;
  private readonly _inputData$: Observable<DSVRowArray | null>;
  public readonly tableData$: Observable<any[] |null>;
  public readonly tableDataNormalized$: Observable<any[] |null>;
  private _transformedData: RawCell[][];
  private _dataTree: any;
  private readonly _tableStructure?: ExportTableStructure;

  private _metaData: ExportTableMetadata;
  private _tableDataNormalized: RawCell[][] | null = null;

  public colHeaders: string[] = [];
  public showExportTable: boolean;

  private readonly decodedDashboardId!: DecodedDashboardId

  constructor({inputData, tableStructure, metaData}: {
    inputData: Observable<DSVRowArray | null>;
    tableStructure?: ExportTableStructure;
    metaData: ExportTableMetadata;
  }) {
    this.decodedDashboardId = DashboardUtils.decodeDashboardId(metaData.dashboardId);
    console.log('[ExportController] Structure:', tableStructure);
    this.showExportTable = tableStructure !== undefined;
    this._tableDataSubject = new BehaviorSubject<any[] | null>(null);
    this.tableData$ = this._tableDataSubject.asObservable();
    this._tableDataNormalizedSubject = new BehaviorSubject<any[] | null>(null);
    this.tableDataNormalized$ = this._tableDataNormalizedSubject.asObservable();
    this._inputData$ = inputData;
    this._tableStructure = tableStructure;
    this._metaData = metaData;
    this._transformedData = [];
    this._dataTree = {};

    if (this.showExportTable) {
      this._subscribeAndTransformData();
    }
  }

  _convertDataArrayRowsToObjectRows(data: any[] | null): any[] {
    if (data === null) {
      return [];
    }

    const dataObject: any[] = [];
    data.forEach((row) => {
      const rowObject: any = {};
      Object.keys(row).forEach((key) => {
        rowObject[key] = row[key];
      });
      dataObject.push(rowObject);
    });
    return dataObject;
  }

  _subscribeAndTransformData() {
    this._inputData$.pipe(map((data) => {
      console.log('[ExportController] Data received:', data);
      if (data === null) {
        return null;
      }
      return this._transformData(data);
    })).subscribe((data) => {
      console.log('[ExportController] Data transformed:', data);
      // const converted = this._convertDataArrayRowsToObjectRows(data);
      // console.log('[ExportController] Data converted:', converted);
      this._tableDataSubject.next(data);

      if (data && data.length) {
        const normalizedData = this.normalizeData(data);
        this._tableDataNormalized = normalizedData
        this._tableDataNormalizedSubject.next(normalizedData);
      }
    });
  }

  get tableStructure(): ExportTableStructure | undefined {
    return this._tableStructure;
  }

  _transformData(data: DSVRowArray): RawCell[][] {
    this.colHeaders = [];

    this._dataTree = this._handleStructureLevel(this.tableStructure!, data);
    this._transformedData = [];
    this._convertTreeToTable(this._dataTree, [])

    return this._transformedData;
  }

  _convertTreeToTable(treePart: any, currentEntry: any[], parentRowSpan: number = 1, ownCn: number = 1) {
    let cn = 1;
    for (const [key, value] of Object.entries(treePart)) {
      const levelRowSpan = Object.keys(value!).length;
      if (typeof (value) !== 'object') {
        // ToDo: Just in special cases; talk to Lion if this case can happen
        currentEntry.push({ value: value, rowSpan: 1 });
        this._transformedData.push(currentEntry);
      } else {
        if (key === '__values') {
          for (const [key, val] of Object.entries(treePart['__values'])) {
            if (!this.colHeaders.includes(key)) { this.colHeaders.push(key); }
            currentEntry.push({ value: val, rowSpan: 1, cn: cn, colSpan: 1 });
          }
          this._transformedData.push(currentEntry);
          return;
        }

        // if (cn >= parentRowSpan && parentRowSpan > 1) {
        if (cn > 1 && cn <= parentRowSpan && parentRowSpan > 1) {
          this._convertTreeToTable(value, [{ value: key, rowSpan: this._countNestedObjects(value!), cn: cn, colSpan: 1 }], levelRowSpan, cn)
        } else {
          this._convertTreeToTable(value, [...currentEntry, { value: key, rowSpan: this._countNestedObjects(value!), cn: cn, colSpan: 1 }], levelRowSpan, cn)
        }

        cn += 1;
      }
    }
  }

  _countNestedObjects(obj: { [key: string]: any }, excludeKey: string = "__values"): number {
    let count = 0;

    // Function to check if the object is a "leaf" object (no nested objects)
    const isLeafObject = (currentObj: { [key: string]: any }): boolean => {
      return Object.keys(currentObj).every(key =>
        typeof currentObj[key] !== 'object' || currentObj[key] === null || key === excludeKey
      );
    };

    // Function to recursively find and count leaf objects
    const recurse = (currentObj: { [key: string]: any }) => {
      for (const key in currentObj) {
        if (!currentObj.hasOwnProperty(key)) continue;
        const value = currentObj[key];
        if (typeof value === 'object' && value !== null) {
          if (key !== excludeKey) {
            if (isLeafObject(value)) {
              count++; // Increment count if it's a leaf object
            } else {
              recurse(value); // Otherwise, recurse into the nested object
            }
          }
        }
      }
    };

    recurse(obj); // Start the recursion with the initial object
    return count > 0 ? count : 1; // Return the count, or 1 if there are no nested objects
  }
  _handleStructureLevel(structure: ExportTableStructure, data: DSVRowArray | any[]): {[key: string]: any} {
    if (structure.header) {
      if (structure.header === '__null_header') {
        this.colHeaders.push('');
      } else if (!this.colHeaders.includes(structure.header)) { this.colHeaders.push(structure.header) }
    }

    const subTree: {[key: string]: any} = {};
    if (structure.isStatic) {
      for (const [key, value] of Object.entries(structure.identifiers)) {
        if (value.valueKey) {
          subTree[value.label!] = { __values: {} };
          data.forEach((d) => {
            subTree[value.label!].__values[d['period_id'] || d['year']] = d[value.valueKey!];
          });
        } else {
          if (value.structure) {
            subTree[value.label!] = this._handleStructureLevel(value.structure, data);
          } else {
            subTree[value.label!] = {};
          }
        }
      }
      return subTree;
    } else {
      const identifier = structure.identifiers[0];
      if (identifier.valueKey) {
        const keys = new Set(d3.group(data, d => d[identifier.labelKey!]).keys());
        // ToDo: Check if iteration is necessary
        keys.forEach((key) => {
          subTree[key] = { __values: {} };
          const subData = data.filter((d) => d[identifier.labelKey!] === key);
          subData.forEach((d) => {
            if (identifier.valueLabelKey) {
              subTree[key].__values[d[identifier.valueLabelKey]] = d[identifier.valueKey!]
            } else {
              subTree[key].__values[d['period_id'] || d['year']] = d[identifier.valueKey!];
            }
          });
        });
        return subTree;
      } else {
        const keys = new Set(d3.group(data, d => d[identifier.labelKey!]).keys());
        keys.forEach((key) => {
          const subData = data.filter((d) => d[identifier.labelKey!] === key);
          if (identifier.structure) {
            subTree[key] = this._handleStructureLevel(identifier.structure!, subData);
          } else {
            subTree[key] = {};
          }
        });
      }
      return subTree;
    }
  }

  setMetadata(key: keyof ExportTableMetadata, value: string) {
    this._metaData[key] = value;
  }

  _getMetricNameFromDecodedDashboardId(): string | undefined {
    let prefix = '';
    let metricName = '';

    switch (this.decodedDashboardId.metricId) {
      case 1:
        metricName = 'Unternehmen';
        break;
      case 2:
        metricName = 'Umsatz';
        break;
      case 5:
        metricName = 'Beschäftigte';
        break;
      case 7:
        metricName = 'Solo-Selbstständige';
        break;
    }

    switch (this.decodedDashboardId.analysisId) {
      case 5:
        prefix = 'Prognose'
        break;
    }

    return [prefix, metricName].filter((val => Boolean(val))).join(' ');
  }

  _getDataSourceFromDecodedDashboardId(): string | undefined {
    switch (this.decodedDashboardId.metricId) {
      case 1:
        return 'DESTATIS';
      case 2:
        return 'DESTATIS';
      case 5:
        return 'Bundesagentur für Arbeit';
      default:
        return undefined;
    }
  }

  stateMetadata = new Map<number, [string, string]>([
    [8, ['Bundesland', 'Baden-Württemberg']],
    [10, ['Bundesland', 'Bayern']]
  ]);

  _getSheetMetadata(dashboardId: string, dashboardVariant?: string, filterValues?: Map<string, any>, filterController?: DashboardFilterController) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentDate = now.toLocaleDateString('de-DE');
    const dashboardPath = dashboardVariant ? `${dashboardId}/${dashboardVariant}` : dashboardId;
    const dashboardIdExportMetadata = dashboardVariant ? `${dashboardId}-${dashboardVariant}` : dashboardId;

    const sheetMetadata = [
      ['Produkt', this._metaData.productName],
      ['Kennzahl', this._getMetricNameFromDecodedDashboardId()],
      ['Stand', currentDate],
      ['Quelle', `Goldmedia Standortmonitor ${currentYear}`],
      ['Online:', 'https://v3.standortmonitor.net/dashboards/' + dashboardPath],
      ['Dashboard-ID:', dashboardIdExportMetadata],
    ].filter((row) => row[1] !== undefined);

    // If dashboardId, filterValues, and filterController are provided,
    // then build metadata from those three and insert it between 'Kennzhal' and 'Stand'
    if (dashboardId && filterValues && filterController) {
      const metadataFromFilterValues = this.buildMetadataFromFilters(filterValues, filterController, dashboardId);
      sheetMetadata.splice(3, 0, ...metadataFromFilterValues);
    }

    if (this._metaData.exportSide) {
      sheetMetadata.push(['', ''])
      sheetMetadata.push(['Export:', this._metaData.exportSide])
    }

    return sheetMetadata;
  }

  private buildMetadataFromFilters(filterValues: Map<string, any>, filterController: DashboardFilterController, dashboardId: string) {
    const metadataFromFilterValues: [string, string][] = [];

    // iterate filterValues to generate metadata
    const displayedFilterValues = Array.from(filterValues.entries()).filter(
      ([key]) => (filterController.getFilterByName(key).uiSettings.showInFilterComponent ?? true)
    );

    displayedFilterValues.forEach(([key, value]) => {
      const filter = filterController.getFilterByName(key);
      const metadataLabel = filter.uiSettings.label;

      if (filter instanceof DashboardMultiSelectFilter) {
        const valueLength = value.length;
        const optionsLength = filter._flatOptionsFilteredByParentFilter.length;

        if (valueLength === optionsLength) {
          const metadataValue = 'Alle';
          metadataFromFilterValues.push([metadataLabel, metadataValue]);
        }
        else {
          const valueLabels = filter._flatOptionsFilteredByParentFilter
            .filter(option => value.includes(option.value))
            .map(option => option.label);
          const metadataValue = valueLabels.join(', ');
          metadataFromFilterValues.push([metadataLabel, metadataValue]);
        }
      }
      else if (filter instanceof DashboardSingleSelectFilter) {
        const metadataValue = filter._flatOptionsFilteredByParentFilter.find(option => option.value === value).label;
        metadataFromFilterValues.push([metadataLabel, metadataValue]);
      }
      else if (filter instanceof DashboardRangeFilter) {
        // For the forecasts, split years range filter value into a regular and forecast
        if (filter.name === 'years' && this.decodedDashboardId.analysisId === 5) {
          // get the regular years from this.colHeaders, assuming colHeaders includes years
          const regularYears = this.colHeaders.filter(header => !isNaN(Number(header)))
          const regularYearsMetadataValue = regularYears[0] + '-' + regularYears[regularYears.length - 1];
          metadataFromFilterValues.push([metadataLabel, regularYearsMetadataValue]);

          // get the forecasted years from this.colHeaders, assuming colHeaders includes years prefixed with '**'
          const forecastMetadataLabel = 'Goldmedia Prognose'
          const forecastYears = this.colHeaders.filter(header => header.startsWith('**') && !isNaN(Number(header.slice(2)))).map(header => header.slice(2))
          const forecastYearsMetadataValue = forecastYears[0] + '-' + forecastYears[forecastYears.length - 1];
          metadataFromFilterValues.push([forecastMetadataLabel, forecastYearsMetadataValue]);
        } else {
          const metadataValue = value.join('-')
          metadataFromFilterValues.push([metadataLabel, metadataValue]);
        }
      }
    });

    // Add state metadata if the first digit of the dashboardId is a regionalId registered at this.stateMetadata
    const maybeRegionalId = this.decodedDashboardId.regionalId ?? this.decodedDashboardId.analysisId;
    if (maybeRegionalId && this.stateMetadata.has(maybeRegionalId)) {
      metadataFromFilterValues.unshift(this.stateMetadata.get(maybeRegionalId)!);
    }

    return metadataFromFilterValues;
  }

  exportTableToExcel(params: {
    data?: RawCell[][],
    filterValues?: Map<string, any>,
    filterController?: DashboardFilterController,
    footnotes?: RawCell[][]
  }) {
    const { filterValues, filterController, data: externalData, footnotes = [] } = params;
    /**
     * Export the html table to an Excel file
     * This method is not used in the current implementation
     * as it does not support virtual scrolling
     */
    // let element = document.getElementById('export-table');
    // const dataWorksheet = xlsx.utils.table_to_sheet(element);

    // Allow for external data to be passed in for export to bypass internal data processing
    // externalData is expected to include the header row as well
    const dataToUse = !externalData ? this._tableDataNormalized! : externalData;
    const headerToUse = !externalData ? this.colHeaders : undefined;
    /**
     * Export the transformed data to an Excel file
     */
    const sheetData = this.generateSheetData(dataToUse, headerToUse, footnotes);
    const merges = this.generateMerges([...dataToUse, ...footnotes]);
    const dataWorksheet = xlsx.utils.aoa_to_sheet(sheetData, {
      sheetStubs: true,
    });
    dataWorksheet['!merges'] = merges;

    const metaDataInput = this._getSheetMetadata(this._metaData.dashboardId, this._metaData.dashboardVariant, filterValues, filterController) || [];
    const metaDataWorksheet = xlsx.utils.aoa_to_sheet(metaDataInput);

    const workbook = xlsx.utils.book_new();

    xlsx.utils.book_append_sheet(workbook, metaDataWorksheet, `Info`);
    xlsx.utils.book_append_sheet(workbook, dataWorksheet, `Export - ${this._metaData.dashboardId}`);

    workbook.Props = {
      Title: `Export ${this._metaData.dashboardId}`, //  - ${this._metaData.dashboardName}
      Author: "Standortmonitor.net",
      Company: "Goldmedia Strategy Consulting GmbH",
    };

    const fileName = this.generateExportFileName();

    xlsx.writeFile(workbook, fileName, { cellStyles: true });
  }

  generateSheetData(data: RawCell[][], header: string[] = [], footnotes: RawCell[][] = []) {
    const sheetHeaders = header?.map(value => this.formatCellValue(value, cellHeaderFormat)) ?? [];
    const sheetBody = data.map((row) => row.map(({ value }) => this.formatCellValue(value, cellBodyFormat)))
    const sheetFootnotes = footnotes.map((row) => row.map(({ value }) => this.formatCellValue(value, cellBodyFormat)))
    return [sheetHeaders, ...sheetBody, ...sheetFootnotes];
  }

  formatCellValue(value: string | null | undefined, formattingStrategies: ICellFormattingStrategies) {
    if (value === null || value === undefined || value.trim() === '') {
      return formattingStrategies.handleEmpty();
    } else if (!isNaN(Number(value))) {
      return formattingStrategies.handleNumber(value);
    } else if (value.startsWith('*') && !isNaN(Number(value.slice(2)))) {
      return formattingStrategies.handleNumberWithSymbol(value);
    } else if (/^-?\d{1,3}(?:\.\d{3})*(?:,\d+)?\s*%$/.test(value)) {
      return formattingStrategies.handlePercentString(value);
    } else if (value.includes('WZ08')) {
      return formattingStrategies.handleWzId(value);
    } else if (value === '[br]') {
      return formattingStrategies.handleLineBreak(value);
    } else {
      return formattingStrategies.handleString(value);
    }
  }

  normalizeData(data: RawCell[][]) {
    // Clone the original data to avoid mutating the input directly
    let normalizedData = data.map(row => row.map(cell => ({ ...cell })));

    // Iterate through each row
    for (let rowIdx = 0; rowIdx < data.length; rowIdx++) {
      // Iterate through each cell in the row
      for (let colIdx = 0; colIdx < data[rowIdx].length; colIdx++) {
        const cell = data[rowIdx][colIdx];
        // If the cell's rowSpan is greater than 1, set the corresponding cells in the next rows to an empty cell
        if (cell.rowSpan > 1) {
          for (let spanIdx = 1; spanIdx < cell.rowSpan; spanIdx++) {
            normalizedData[rowIdx + spanIdx].splice(colIdx, 0, { value: null, rowSpan: 0, cn: 0, colSpan: 0 });
          }
        }
      }
    }
    return normalizedData;
  }

  generateMerges(data: RawCell[][]) {
    let merges: MergeObject[] = [];

    for (let rowIdx = 0; rowIdx < data.length; rowIdx++) {
      for (let cellIdx = 0; cellIdx < data[rowIdx].length; cellIdx++) {
        const cell = data[rowIdx][cellIdx];

        // Handle rowSpan
        if (cell.rowSpan > 1) {
          const merged = {
            s: { r: rowIdx + 1, c: cellIdx }, // + 1 to account for header row
            e: { r: rowIdx + cell.rowSpan, c: cellIdx }
          };
          merges.push(merged);
        }

        // Handle colSpan
        if (cell.colSpan > 1) {
          const merged = {
            s: { r: rowIdx + 1, c: cellIdx },
            e: { r: rowIdx + 1, c: cellIdx + cell.colSpan - 1 }
          };
          merges.push(merged);
        }
      }
    }

    return merges;
  }

  exportTableToCsv(params: {
    data?: RawCell[][],
    filterValues?: Map<string, any>,
    filterController?: DashboardFilterController,
    footnotes?: RawCell[][]
  }) {
    const { filterValues, filterController, data: externalData, footnotes = [] } = params;
    
    // Allow for external data to be passed in for export to bypass internal data processing
    // externalData is expected to include the header row as well
    const dataToUse = !externalData ? this._tableDataNormalized! : externalData;
    const headerToUse = !externalData ? this.colHeaders : undefined;
    
    // Get metadata
    const metaDataInput = this._getSheetMetadata(this._metaData.dashboardId, this._metaData.dashboardVariant, filterValues, filterController) || [];
    
    // Generate sheet data (includes headers, body, and footnotes)
    const sheetData = this.generateSheetData(dataToUse, headerToUse, footnotes);
    
    // Convert all values to strings for CSV format (formatCellValue may return objects for Excel styling)
    const csvCompatibleMetadata = metaDataInput.map(row => 
      row.map(cell => String(cell ?? ''))
    );
    
    const csvCompatibleData = sheetData.map(row => 
      row.map(cell => {
        if (cell === null || cell === undefined) return '';
        if (typeof cell === 'object' && 'v' in cell) return String(cell.v ?? '');
        return String(cell);
      })
    );
    
    // Combine metadata + blank line + sheet data
    const csvData: string[][] = [
      ...csvCompatibleMetadata,
      [], // blank line separator between metadata and data table
      ...csvCompatibleData
    ];
    
    // Convert to CSV format using d3
    const csvContent = d3.csvFormatRows(csvData);
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fileName = this.generateExportFileName().replace('.xlsx', '.csv');
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
  }

  generateExportFileName(): string {
    const nameParts = [
      getFilenameDatePrefix(),
      'StaMo',
      `Export ${this._metaData.dashboardId}`
    ];

    if (this._metaData.exportSide) {
      nameParts.push(`Export ${this._metaData.exportSide}`);
    }

    return nameParts.join(' - ') + '.xlsx';
  }
}
